/**
 * DisbursementQuoteChoice — BM-H5023 (3 Nov 2026).
 *
 * On the payment request form. When a client account (CTO) or third-party
 * payment is for a matter with a disbursement quote awaiting money on
 * account, the requester must say whether it pays that quote. Vanessa chose
 * a hard block: a quote still awaiting MOA cannot be paid. The server makes
 * the same check on submit, edit and approval; this is so nobody fills the
 * whole form in first.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config/api";
import { AlertTriangle } from "lucide-react";

export const GATED_TYPES = ["CTO", "Third-Party"];
export const NOT_FOR_A_QUOTE = "none";

/** Step 1 error for the current choice, or "" when it may go on. */
export function choiceProblem(quotes, choice) {
  if (!quotes || quotes.length === 0) return "";
  if (!choice) return "Say whether this payment is for the disbursement quote awaiting money on account";
  if (choice === NOT_FOR_A_QUOTE) return "";
  const q = quotes.find((x) => x.id === choice);
  if (q && !q.payment_allowed) {
    return `Money on account for ${q.supplier} has not been received. This payment cannot be made until accounts links it.`;
  }
  return "";
}

export default function DisbursementQuoteChoice({ matterRef, paymentType, value, onChange, onQuotes }) {
  const [quotes, setQuotes] = useState([]);
  const [err, setErr] = useState("");
  const gated = GATED_TYPES.includes(paymentType);
  const ref = (matterRef || "").trim();

  useEffect(() => {
    let live = true;
    setErr("");
    if (!gated || !ref) { setQuotes([]); onQuotes && onQuotes([]); return undefined; }
    axios.get(`${API}/matters/${encodeURIComponent(ref)}/disbursement-quotes`)
      .then((res) => {
        if (!live) return;
        const awaiting = (res.data.quotes || []).filter((q) => q.status === "awaiting_moa");
        setQuotes(awaiting);
        onQuotes && onQuotes(awaiting);
      })
      .catch(() => {
        if (!live) return;
        setQuotes([]);
        onQuotes && onQuotes([]);
        setErr("Could not check this matter for disbursement quotes. The check is made again when you submit.");
      });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gated, ref]);

  if (err) return <p className="text-xs text-status-amber mt-2">{err}</p>;
  if (!gated || quotes.length === 0) return null;
  const problem = choiceProblem(quotes, value);

  return (
    <div className="mt-2 p-3 rounded-md border border-amber-300 bg-status-amber-dim" data-testid="pr-disb-quote">
      <p className="text-sm font-medium flex items-center gap-1.5">
        <AlertTriangle className="w-4 h-4 text-status-amber" />
        This matter has a disbursement quote awaiting money on account. Is this payment for it?
      </p>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
        data-testid="pr-disb-quote-select"
      >
        <option value="">Choose</option>
        {quotes.map((q) => (
          <option key={q.id} value={q.id}>
            Yes: {q.supplier}, {q.description} (£{Number(q.outstanding).toFixed(2)} MOA still needed)
          </option>
        ))}
        <option value={NOT_FOR_A_QUOTE}>No, it is for something else</option>
      </select>
      {value && problem && <p className="text-xs text-status-red mt-1" data-testid="pr-disb-quote-blocked">{problem}</p>}
    </div>
  );
}
